/**
 * FluxNode Types and Interfaces
 *
 * Type definitions for FluxNode monitoring, rewards tracking,
 * and parallel assets management on the Flux blockchain network.
 */

// Node Tiers
export type NodeTier = 'CUMULUS' | 'NIMBUS' | 'STRATUS';

// Node Status
export type NodeStatus = 'CONFIRMED' | 'STARTED' | 'OFFLINE' | 'EXPIRED' | 'DOS_BASE';

// Benchmark Status
export type BenchmarkStatus = 'PASSED' | 'FAILED' | 'TOASTER';

// Parallel Asset Chains
export type ParallelChain = 'ETH' | 'BSC' | 'KDA' | 'SOL' | 'AVAX' | 'MATIC' | 'TRX' | 'BNB';

/**
 * Core FluxNode Interface
 */
export interface FluxNode {
  // Node identification
  txhash: string;
  outidx: string;
  ip: string;
  network: string;

  // Node configuration
  tier: NodeTier;
  payment_address: string;
  pubkey: string;
  collateral: string;

  // Status and health
  added_height: number;
  confirmed_height: number;
  last_confirmed_height: number;
  last_paid_height: number;

  // Time tracking
  activesince: string;
  lastpaid: string;

  // Ranking and rewards
  rank: number;
  roundtime: number;

  // Computed properties
  status?: NodeStatus;
  uptime?: number;
  benchmarkStatus?: BenchmarkStatus;
}

/**
 * Benchmark Data Interface
 */
export interface BenchmarkData {
  status: BenchmarkStatus;
  bench: string;
  flux_version: string;

  // Performance metrics
  cores?: number;
  ram?: number;
  ssd?: number;
  ddwrite?: string;
  eps?: number;
  ping?: number;
  download_speed?: number;
  upload_speed?: number;

  // Timing
  time?: number;
  real_cores?: number;
}

/**
 * Collateral Information
 */
export interface CollateralInfo {
  txid: string;
  outputIndex: number;
  amount: number;
  confirmations: number;
  tier: NodeTier;
}

/**
 * Node Rewards Information
 */
export interface NodeRewards {
  nodeId: string;
  daily: number;
  weekly: number;
  monthly: number;
  yearly: number;
  lastPayout: Date;
  totalEarned: number;
  pendingRewards: number;
  roi: number;
  projectedROI: number;
}

/**
 * Parallel Asset Interface
 */
export interface ParallelAsset {
  chain: ParallelChain;
  symbol: string;
  contractAddress: string;
  amount: number;
  valueUSD: number;
  valueFLUX: number;
  claimable: boolean;
  lastClaim?: Date;
  pendingClaims: number;
}

/**
 * Wallet Configuration
 */
export interface FluxWallet {
  id: string;
  address: string;
  label?: string;
  addedAt: Date;
  nodes?: FluxNode[];
}

/**
 * Network Statistics
 */
export interface NetworkStats {
  totalNodes: number;
  cumulusCount: number;
  nimbusCount: number;
  stratusCount: number;
  totalCollateral: number;
  networkHashrate?: number;
  blockHeight: number;
  fluxPrice: number;
}

/**
 * Reward Projection
 */
export interface RewardProjection {
  estimated: number;
  conservative: number;
  optimistic: number;
  confidence: number;
  days: number;
}

/**
 * API Response Types
 */
export interface FluxAPIResponse<T> {
  status: 'success' | 'error';
  data?: T;
  error?: string;
}

/**
 * Node List Response from API
 */
export interface NodeListResponse {
  status: 'success' | 'error';
  data?: FluxNode[];
}

/**
 * Dashboard Summary
 */
export interface DashboardSummary {
  totalNodes: number;
  activeNodes: number;
  offlineNodes: number;
  totalValue: number;
  monthlyRevenue: number;
  avgROI: number;
  parallelAssetsValue: number;
}

/**
 * Tier Requirements (Collateral amounts in FLUX)
 */
export const TIER_REQUIREMENTS = {
  CUMULUS: {
    collateral: 1000,
    cpu: 2,
    ram: 8,
    ssd: 220,
    bandwidth: 30,
    color: '#3b82f6' // Blue
  },
  NIMBUS: {
    collateral: 12500,
    cpu: 4,
    ram: 32,
    ssd: 440,
    bandwidth: 60,
    color: '#8b5cf6' // Purple
  },
  STRATUS: {
    collateral: 40000,
    cpu: 8,
    ram: 64,
    ssd: 880,
    bandwidth: 100,
    color: '#ec4899' // Pink
  }
} as const;

/**
 * Node Status Colors
 */
export const NODE_STATUS_COLORS = {
  CONFIRMED: '#10b981', // Green
  STARTED: '#f59e0b',   // Orange
  OFFLINE: '#ef4444',   // Red
  EXPIRED: '#6b7280',   // Gray
  DOS_BASE: '#ef4444'   // Red
} as const;

/**
 * Benchmark Status Colors
 */
export const BENCHMARK_STATUS_COLORS = {
  PASSED: '#10b981',  // Green
  FAILED: '#ef4444',  // Red
  TOASTER: '#f59e0b'  // Orange
} as const;
